import {
  CACHING_PREFIX,
  clearChannelCache,
  clearEpgCacheDays,
  clearFilePrices,
  DynamicDataTypeEnum,
  FFUser,
  KuxCategoriesDTO,
  KuxConfig,
  KuxDms,
  kuxSetConfig,
  kuxUpdateConfig,
  LS_ROOT_CATEGORY_ID,
  PERFORMANCE_FEATURE,
  PresentationAdditionalTypeEnum,
  QuotaStatusEnum,
  setEpgLimitCache,
  tvpilGetLocalCache,
  tvpilSetCacheService,
  UIDAPresentationTypeEnum,
  UIDAStructureTypeEnum,
  updateDynamicData,
  setEpgHoursPartial,
  getCacheValue,
  DEFAULT_SERVER_TIMEOUT,
  HouseholdMigrationErrorMessage,
  DEFAULT_CONNECTIVITY_INTERVAL,
  DEFAULT_APPLICATION_RECOVERY_DELAY,
} from '@kaltura-ott/tvpil';
import { createAppStoreEvent, setJournalMeta, tvpilJournal } from '@kaltura-ott/tvpil-utils';
import { action, observable, observe, computed, when, IReactionDisposer } from 'mobx';
import { lastValueFrom } from 'rxjs';
import { Lifecycle, container, singleton } from 'tsyringe';

import { ConcurrencyStoreFacade } from '../../facades/concurrency-store-facade';
import { CookiesStoreFacade } from '../../facades/cookies-store-facade';
import { DeviceStoreFacade } from '../../facades/device-store-facade';
import { GridPageStoreFacade } from '../../facades/grid-page-store-facade';
import { LocalizationStoreFacade } from '../../facades/localization-store-facade';
import { LoginStoreFacade } from '../../facades/login-store-facade';
import { ParentalPinStoreFacade } from '../../facades/parental-pin-store-facade';
import { ParentalPinStoreFacadeDepreciated } from '../../facades/parental-pin-store-facade-depreciated';
import { PlayoutStoreFacade } from '../../facades/playout-store-facade';
import { RecordRepositoryFacade } from '../../facades/record-repository-facade';
import { ServicesStoreFacade } from '../../facades/services-store-facade';
import { ToastStoreFacade } from '../../facades/toasts-store-facade';
import { TransactionStoreFacade } from '../../facades/transaction-store-facade';
import { AppStoreInterface } from '../../interfaces/app';
import { ONE_HOUR_MILLISECONDS, ONE_SECOND_MILLISECONDS, clearRhinoCache } from '../../rhino';
import { fetchFavoriteChannels } from '../../rhino/data/favorite';
import { onRent } from '../../rhino/data/invalidations';
import { fetchPersonalLineup } from '../../rhino/data/personalLineup';
import { setRhinoStaleTime } from '../../rhino/data/queryClient/queryClient';
import { updateRhinoConfig } from '../../rhino/init/rhinoConfig';
import {
  asyncSentryLogger,
  FEATURE_ENABLER_VARIATIONS,
  featureEnabler,
  Analytics,
  isSessionStorageSupported,
} from '../../utils';
import { convertRhinoErrorToTvpil } from '../../utils/error';
import { assertPresence, errorHandling } from '../../utils/error-handling';
import { tvpilVersion } from '../../version';
import {
  CategoryChildren,
  EpgStore,
  ExponeaStore,
  ExponeaUiResponse,
  HeroRailStoreV2,
  clearNetworkCache,
} from '../category';
import { RootStore } from '../category/root';
import { RailStoreV2 } from '../category/widgets/rails/rail-store-v2';
import { channelStats, ChannelStatsStore } from '../channelStats/channel-stats-store';
import { LastWatchedLinearStore } from '../channelStats/last-watched-linear-store';
import { Features } from '../config';
import { DebugMenuStore } from '../debugMenu';
import { clearDebugDataStorage, mergeConfigs } from '../debugMenu/debugMenu.service';
import { DmsStore } from '../dms';
import { ErrorStore, ErrorPriority, AppApiErrorList } from '../error';
import type { FavoriteRepository } from '../favoriteRepository/favorite-repository';
import { KeyboardStore } from '../keyboard';
import { TopShelfStore } from '../topShelf/top-shelf-store';
import type { LoginStore } from '../user/login-store';
import { UserStore } from '../user/user-store';

import { AppParams } from './app-params';
import { AppParametersStore } from './app-params-store';
import {
  bootApp,
  bootAppOffline,
  createRootStore,
  preloadAfterLoad,
  resetWidgets,
  reInitWidget,
  setupDMS,
  suggestedRecalculation,
  checkActualCache,
  lastFetchCategoryTreeTimestamp,
  createCategoryTree,
  reInitTotalNpvrRail,
  CUSTOMER_LOGO_CACHE_KEY,
  getPromoAssets,
  PromoAssets,
} from './app-services';
import { registerContainers } from './register-containers';

const SUGGESTED_RECALCULATION_TIMEOUT = 1000 * 60 * 1;
const APPLICATION_ROUTE_STORAGE_KEY = 'applicationRoute';

/**
 * Entry point store for applications. Contains all other stores
 * Usage:
 * ```typescript
 * const appStore = new AppStore({
 *   partnerId: xxxx,
 *   applicationName: '',
 *   clientVersion: '0.0.0',
 *   platform: '',
 *   udid: '',
 *   clientTag: '',
 *   tag: '',
 *   endpoint: '',
 * });
 * ```
 * @category Store
 */

export interface CookiePopupDetails {
  loadExternalCookiePopup?: boolean;
  externalPopupUrl?: string;
}

@singleton()
export class AppStore implements AppStoreInterface {
  static moduleName = 'AppStore';

  @observable.ref categoryTree?: KuxCategoriesDTO;
  @observable rootStore!: RootStore;
  @observable topShelfStore?: TopShelfStore;
  @observable debugMenuStore?: DebugMenuStore;

  @observable staticAssetsUrl?: string;
  @observable isReady = false;
  @observable isForceUpdate = false;
  @observable forcedUpdateURL = '';
  @observable _applicationRoute?: string = undefined;
  @observable customerLogo?: string;
  @observable cookiePopupDetails: CookiePopupDetails = {};
  @observable promoAssets?: PromoAssets = {};
  @observable categoryTreeUpdatedAt = 0;

  @computed public get params(): AppParams {
    return this.appParamsStore.params;
  }

  @computed public get primeTime(): number | undefined {
    return EpgStore.primeTime;
  }
  @computed public get dms(): KuxDms {
    return this.dmsStore.dms;
  }

  @computed public get apiError(): AppApiErrorList | null {
    return this.errorStore.apiError;
  }

  @computed public get apiErrorRetrying() {
    return this.errorStore.isRetrying;
  }

  @computed public get applicationRoute(): string | undefined | null {
    if (isSessionStorageSupported()) {
      return window.sessionStorage.getItem(APPLICATION_ROUTE_STORAGE_KEY);
    }

    return this._applicationRoute;
  }

  @observable.ref channelStats?: ChannelStatsStore;

  private suggestedRecalculationTimeout?: NodeJS.Timeout;
  private disposer: IReactionDisposer | undefined;
  private appParamsStore: AppParametersStore;
  private dmsStore: DmsStore;
  private isInitialising = false;
  public lastWatchedLinear: LastWatchedLinearStore;
  public userStore: UserStore;
  public favoriteRepository: FavoriteRepository;

  constructor(
    // internal
    private errorStore: ErrorStore,

    // public for platforms
    public cookieStore: CookiesStoreFacade,
    public concurrencyStore: ConcurrencyStoreFacade,
    public loginStore: LoginStoreFacade,
    public deviceStore: DeviceStoreFacade,
    public servicesStore: ServicesStoreFacade,
    public recordRepository: RecordRepositoryFacade,
    public toastStore: ToastStoreFacade,
    //@depreciated
    public parentalPinStoreDepreciated: ParentalPinStoreFacadeDepreciated,
    public parentalPinStore: ParentalPinStoreFacade,
    public playoutStoreV2: PlayoutStoreFacade,
    public keyboardStore: KeyboardStore,
    public localizationStore: LocalizationStoreFacade,
    public gridPageStoreV2: GridPageStoreFacade,

    public transactionStore: TransactionStoreFacade,
    public exponeaStore: ExponeaStore,
    public kuxAnalytics: Analytics,
  ) {
    this.favoriteRepository = container.resolve<FavoriteRepository>('favoriteRepository');
    this.appParamsStore = container.resolve<AppParametersStore>('appParamsStore');
    this.dmsStore = container.resolve<DmsStore>('dmsStore');
    this.lastWatchedLinear = container.resolve<LastWatchedLinearStore>('lastWatchedLinearStore');
    this.userStore = container.resolve<UserStore>('userStore');
  }

  get _tvpilParentalPinStore() {
    if (this.params?.useParentalPinStoreDepreciated) {
      return this.parentalPinStoreDepreciated;
    }
    return this.parentalPinStore;
  }

  public setJwtCookie(jwtToken: { jwt: string; refreshToken: string }) {
    const loginStore = container.resolve<LoginStore>('loginStore');
    loginStore.initialJwtToken = jwtToken;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  @action
  @errorHandling({
    type: ErrorPriority.high,
    callback: function (this: AppStore, error) {
      convertRhinoErrorToTvpil(error);

      this.isInitialising = false;
    },
  })
  async init(params: AppParams) {
    if (this.isInitialising) {
      return;
    }

    this.isInitialising = true;
    setJournalMeta({
      udid: params.udid,
    });

    tvpilJournal.log(createAppStoreEvent('InitStart'));

    this.appParamsStore.params = params;

    EpgStore.epgScreenLength = params.designParams.epgScreenLength;
    // @ts-expect-error TODO Fix
    EpgStore.skipChannelPlaceholdersGeneration = params.designParams.skipChannelPlaceholdersGeneration;

    this.isReady = false;
    const { cacheService } = params;

    tvpilSetCacheService(cacheService);
    this.customerLogo = (await getCacheValue(CUSTOMER_LOGO_CACHE_KEY)) || '';

    RailStoreV2.defaultSizeIndex = params.designParams.defaultSizeIndex;
    setEpgLimitCache(featureEnabler.variation('epgCacheChannelPerDay', 0));

    DebugMenuStore.customRootID = (await cacheService.getItem(LS_ROOT_CATEGORY_ID)) as number;
    if (params.debugMenu) {
      await mergeConfigs(params);
      this.debugMenuStore = new DebugMenuStore(params.dmsSetting.applicationName);
    } else {
      clearDebugDataStorage(cacheService);
    }

    checkActualCache(cacheService, params.dmsSetting.partnerId, params.dmsSetting.endpoint);

    const configParams = {
      endpointUrl: params.dmsSetting.endpoint,
      clientTag: params.dmsSetting.clientTag,
      brandId: params.deviceAttributes.brandId,
      isDebug: params.debugMenu,
      requestTimeout: DEFAULT_SERVER_TIMEOUT * 1000,
    };

    kuxSetConfig(configParams);

    updateRhinoConfig((state) => ({ ...state, isDebug: Boolean(configParams.isDebug) }));

    const dms = await setupDMS(params);

    if (params.onDmsReady) {
      params.onDmsReady(dms);
    }

    tvpilJournal.log(createAppStoreEvent('DmsReady'));

    this.errorStore.clearAllApiError();

    if (dms.launchDarkly && params?.featureEnabledOptions) {
      const ffUser: FFUser = {
        key: params.udid,
        name: 'anonymous',
        anonymous: true,
        custom: {
          applicationName: params.dmsSetting.applicationName,
          clientTag: params.dmsSetting.clientTag,
          clientVersion: params.dmsSetting.clientVersion,
          consumerEmail: '',
          platform: params.dmsSetting.platform,
          deviceBrand: String(params.deviceAttributes.brandId),
          deviceFamily: String(dms.deviceFamilyId),
          firmware: params.deviceAttributes.firmware, // not used yet
          partnerId: params.dmsSetting.partnerId.toString(),
          release: String(params.release || ''),
          osVersion: String(params.deviceAttributes.osVersion),
          tvpilVersion,
        },
      };

      featureEnabler.setup(
        {
          apiKeys: dms.launchDarkly,
          data: {
            key: params.udid,
            custom: {
              // TODO: could remove `custom` if it deprecated
              applicationName: params.dmsSetting.applicationName,
              clientTag: params.dmsSetting.clientTag,
              clientVersion: params.dmsSetting.clientVersion,
              deviceBrand: String(params.deviceAttributes.brandId),
              partnerId: String(params.dmsSetting.partnerId),
              release: params.release,
              tvpilVersion,
            },
          },
        },
        params.featureEnabledOptions.ffClient,
      );
      await featureEnabler.init(ffUser);

      setRhinoStaleTime(featureEnabler.variation('STALE_TIME', 3 * ONE_HOUR_MILLISECONDS));

      tvpilJournal.log(createAppStoreEvent('LdReady'));
    }

    EpgStore.hoursDownload = featureEnabler.variation('EPG_HOURS_DOWNLOAD', 24);

    setEpgHoursPartial(featureEnabler.variation('EPG_HOURS_DOWNLOAD', 24));
    setEpgLimitCache(featureEnabler.variation('epgCacheChannelPerDay', -1));

    DebugMenuStore.isForceDebug = params.debugMenu;
    HeroRailStoreV2.PERFORMANCE_LAST_WATCHED = featureEnabler.variation(
      'PERFORMANCE_LAST_WATCHED',
      PERFORMANCE_FEATURE.PERFORMANCE_LAST_WATCHED,
    );

    const result = await this.boot(params, false, dms);

    tvpilJournal.log(createAppStoreEvent('InitEnd'));

    this.isInitialising = false;

    return result;
  }

  private async initTransactionStore() {
    // check if analytics section defined and has plugins
    // get plugin instance with its config by using pluginsMap

    await this.transactionStore.originalStore.init();

    tvpilJournal.log(createAppStoreEvent('TransactionsReady'));
  }

  private async checkMigrationStatus() {
    await this.loginStore.originalStore.checkMigrationStatus();

    tvpilJournal.log(createAppStoreEvent('MigrationCheckDone'));

    if (this.loginStore.originalStore.isUserMigrationBlock) {
      throw new Error(HouseholdMigrationErrorMessage);
    }
  }

  @action.bound
  @asyncSentryLogger('AppBoot')
  async boot(params: AppParams, quiteUpdate = false, dms: KuxDms | undefined = this.dms) {
    tvpilJournal.log(createAppStoreEvent('BootStart'));

    this.appParamsStore.params = params;
    this.isReady = false;
    if (this.suggestedRecalculationTimeout) {
      clearTimeout(this.suggestedRecalculationTimeout);
    }

    const {
      oAuthClientSecret,
      oAuthGrantType,
      oAuthScope,
      authTokenPath,
      authDevicePath,
      oAuthRedirectUri,
      oAuthEndpoint,
      plainAuthorizationEndpoint,
      oAuthClientId,
      oAuthCodeChallengeMethod,
      oAuthResponseType,
    } = featureEnabler.variation('AUTH_PARAMS', {});

    if (!featureEnabler.variation('FEATURE_nPVR', false)) {
      this.recordRepository.space.status = QuotaStatusEnum.NpvrServiceNotAllowed;
    }

    const configParams = {
      endpointUrl: params.dmsSetting.endpoint,
      authDevicePath,
      authTokenPath,
      clientTag: params.dmsSetting.clientTag,
      oAuthRedirectUri,
      oAuthEndpoint,
      plainAuthorizationEndpoint,
      oAuthClientId,
      oAuthCodeChallengeMethod,
      oAuthResponseType,
      oAuthScope,
      oAuthGrantType,
      oAuthClientSecret,
      persistCachingTtl: featureEnabler.variation('persistCachingTtl', 0),
      brandId: params.deviceAttributes.brandId,
    };

    if (quiteUpdate) {
      kuxUpdateConfig(configParams as KuxConfig);
    } else {
      kuxSetConfig(configParams);
    }
    this.dmsStore.dms = assertPresence(dms) || assertPresence(await setupDMS(params));
    this.setCookiePopupData();

    //depreciated
    params.useParentalPinStoreDepreciated ? this.parentalPinStoreDepreciated.init() : this.parentalPinStore.init();

    kuxUpdateConfig({
      endpointUrl: this.dmsStore.dms.endpointUrl,
      requestTimeout: this.dmsStore.dms.serverTimeOut * 1000,
    });
    const forcedUpdateKey = featureEnabler.variation('forcedUpdateKey', '');
    if (
      this.dms?.isForceUpdate &&
      forcedUpdateKey &&
      this.dms.forcedUpdateURLObject &&
      this.dms.forcedUpdateURLObject[forcedUpdateKey]
    ) {
      this.isForceUpdate = true;
      this.forcedUpdateURL = this.dms.forcedUpdateURLObject[forcedUpdateKey];
      return;
    }

    let analyticsInitializer;

    if (
      featureEnabler.variation(Features.ANALYTICS, false) &&
      this.dms?.analytics &&
      !!this.dms.analytics.plugins?.length
    ) {
      analyticsInitializer = () => {
        this.kuxAnalytics.init(params, this.dms, this.loginStore.ks);
        if (this.deviceStore?.householdDevice) {
          this.kuxAnalytics.updateUser(this.userStore, this.deviceStore.householdDevice);
        } else {
          observe(this.deviceStore.originalStore, 'householdDevice', () => {
            if (this.deviceStore.originalStore?.householdDevice) {
              if (!this.kuxAnalytics.hasUser) {
                this.kuxAnalytics.updateUser(this.userStore, this.deviceStore.originalStore.householdDevice);
              }
            }
          });
        }

        tvpilJournal.log(createAppStoreEvent('AnalyticsReady'));
      };
    }

    const { categoryTree, rootStore, topShelfStore, customerLogo } = await (params.isOffline
      ? bootAppOffline(params)
      : bootApp(params, assertPresence(this.dms), analyticsInitializer));

    this.customerLogo = customerLogo as string;
    this.categoryTree = categoryTree;
    this.rootStore = assertPresence(rootStore);
    this.topShelfStore = topShelfStore;
    this.staticAssetsUrl = this.dms?.staticAssetsUrl;

    this.suggestedRecalculationTimeout = setTimeout(() => {
      this.suggestedRecalculation();
    }, SUGGESTED_RECALCULATION_TIMEOUT);

    if (this.disposer) this.disposer();

    this.disposer = when(
      () => !this.loginStore.isAnonymous,
      async () => {
        if (featureEnabler.variation('PERFORMANCE_RECORDS', PERFORMANCE_FEATURE.PERFORMANCE_RECORDS)) {
          await this.recordRepository.originalStore.getAllRecordList();
        }
      },
    );

    const promises: Array<Promise<unknown>> = [
      this.setPromoScreen(),
      this.initTransactionStore(),
      this.checkMigrationStatus(),
    ];

    if (!this.loginStore.isAnonymous && rootStore.isPersonalLineupEnabled) {
      promises.push(fetchPersonalLineup());
    }

    if (!this.loginStore.isAnonymous && rootStore.isFavoriteChannelPersonalization) {
      promises.push(fetchFavoriteChannels());
    }

    await Promise.all(promises);

    this.isReady = true;

    tvpilJournal.log(createAppStoreEvent('BootEnd'));

    return this.loginStore.ks;
  }

  async clearAllCache({ rent, skipCategoryTree }: { rent: boolean; skipCategoryTree: boolean }) {
    const cache = tvpilGetLocalCache();
    await cache.removeAllBySubPrefix(CACHING_PREFIX);
    clearFilePrices();
    clearEpgCacheDays();
    clearChannelCache();

    this.favoriteRepository.invalidate();

    if (skipCategoryTree) {
      return;
    }

    if (rent) {
      onRent();
    } else {
      clearRhinoCache({ categoryTree: skipCategoryTree });
    }
  }

  @action
  @errorHandling({
    type: ErrorPriority.high,
    callback: function (this: AppStore, error) {
      convertRhinoErrorToTvpil(error);
    },
  })
  async rebootApp(quiteUpdate = false) {
    tvpilJournal.log(createAppStoreEvent('RebootStart'));

    this.isReady = false;
    await this.clearAllCache({ skipCategoryTree: false, rent: false });
    if (!this.params) {
      this.isReady = true;
      return;
    }
    const result = await this.boot(
      {
        ...this.params,
      },
      quiteUpdate,
    );

    tvpilJournal.log(createAppStoreEvent('RebootEnd'));

    return result;
  }

  @errorHandling({ type: ErrorPriority.high })
  async returnFromSleep() {
    if (
      featureEnabler.variation(Features.USER_MIGRATION_CHECK, false) &&
      !this.loginStore.isAnonymous &&
      this.dms?.isBGMigrationCheckNeeded
    ) {
      await this.userStore?.loadUserData();
      await this.loginStore.originalStore.checkMigrationStatus();
      if (this.loginStore.originalStore.isUserMigrationBlock) {
        throw new Error(HouseholdMigrationErrorMessage);
      }
    }
    await this.transactionStore.loadMainSubscriptions();
  }

  restoreAfterConnectionError() {
    this.parentalPinStore.clearPinCodes();
  }

  @action
  @errorHandling({
    type: ErrorPriority.high,
    callback: function (this: AppStore, error) {
      convertRhinoErrorToTvpil(error);
    },
  })
  async reloadCategoryTree(useOldTree = false) {
    await this.reloadTree(useOldTree);
    if (this.categoryTree) {
      const root = createRootStore(this.categoryTree);
      this.rootStore = root.rootStore;
    }
    this.rootStore?.assetDetailsPages?.init(assertPresence(this.dms));
  }

  private async reloadTree(useOldTree = true) {
    const parseCategoryTree = await createCategoryTree(
      assertPresence(this.dms),
      assertPresence(this.params),
      useOldTree ? this.categoryTree?.id : 0,
    );
    if (!parseCategoryTree) {
      // skip update if category in cache or smth goes wrong
      return;
    }
    const { categoryTree, rootStore } = parseCategoryTree;
    this.categoryTree = categoryTree;
    this.rootStore = rootStore;
    this.categoryTreeUpdatedAt = Date.now();
  }

  @action
  @errorHandling({ type: ErrorPriority.low })
  async refreshData() {
    const ttl = featureEnabler.variation(FEATURE_ENABLER_VARIATIONS.refreshDataTTL, 0);
    if (ttl && Date.now() - lastFetchCategoryTreeTimestamp > ttl) {
      // @ts-expect-error TODO Fix
      const currentCategoryId = this.categoryTree.id;
      await this.reloadTree();

      // Clear cache for new categoryTree
      // @ts-expect-error TODO Fix
      if (currentCategoryId !== this.categoryTree.id) {
        await this.clearAllCache({ skipCategoryTree: true, rent: false });
      }
    }
  }

  /**
   * Loading heroStores and EpgStores. Speeds up first page load
   */
  @errorHandling({ type: ErrorPriority.low })
  public async preloadAfterLoad(
    subtypes?: (UIDAPresentationTypeEnum | PresentationAdditionalTypeEnum)[],
    skipEpgPrograms?: boolean,
  ): Promise<void> {
    if (
      featureEnabler.variation('PERFORMANCE_PRELOAD_AFTER_LOAD', PERFORMANCE_FEATURE.PERFORMANCE_PRELOAD_AFTER_LOAD)
    ) {
      if (this.loginStore.isAnonymous) {
        return;
      }

      await preloadAfterLoad({
        dms: assertPresence(this.dms),
        children: this.rootStore?.children,
        // @ts-expect-error TODO Fix
        subtypes,
        skipEpgPrograms,
      });
    }
  }

  @action
  async resetWidgets({ rent = false }: { rent: boolean }) {
    await this.clearAllCache({ skipCategoryTree: false, rent });
    await this.reInitSearchRail();
    if (this.rootStore?.children) return resetWidgets(this.rootStore.children);
  }

  @action
  async resetBookmarkDependentWidgets() {
    if (this.rootStore?.children)
      return resetWidgets(
        this.rootStore.children,
        [UIDAPresentationTypeEnum.cw, UIDAPresentationTypeEnum.suggested],
        [],
      );
  }

  @action
  async reInitRecommendations() {
    if (this.rootStore?.children)
      return reInitWidget(this.rootStore.children, [UIDAPresentationTypeEnum.suggested], []);
  }

  @action
  async reInitEpg() {
    if (this.rootStore?.children) return reInitWidget(this.rootStore.children, [UIDAPresentationTypeEnum.epg], []);
  }

  @action
  async reInitSearchRail() {
    if (this.rootStore.search && this.rootStore.search.railStore) {
      return reInitWidget([this.rootStore.search.railStore], [UIDAPresentationTypeEnum.collection], []);
    }
  }

  @action.bound
  async reInitRecordings() {
    const filter = (store: CategoryChildren) => store.type === UIDAStructureTypeEnum.widget;
    if (this.rootStore?.children) {
      await reInitTotalNpvrRail(this.rootStore?.children);
      return reInitWidget(this.rootStore.children, [UIDAPresentationTypeEnum.npvr], [], filter);
    }
  }

  @action
  saveApplicationRoute(path: string): string | void {
    if (isSessionStorageSupported()) {
      window.sessionStorage.setItem(APPLICATION_ROUTE_STORAGE_KEY, path);
    } else {
      this._applicationRoute = path;
    }

    return path;
  }

  clearApplicationRoute() {
    if (isSessionStorageSupported()) {
      window.sessionStorage.removeItem(APPLICATION_ROUTE_STORAGE_KEY);
    } else {
      this._applicationRoute = undefined;
    }
  }

  @action
  @errorHandling({ type: ErrorPriority.low })
  async suggestedRecalculation(forceUpdate = false) {
    if (!this.loginStore.isAnonymous) {
      const { suggestedRecalculationPeriod, suggestedCalculationByLastXItems, savedInterestItemsList, mediaTypes } =
        assertPresence(this.dms);
      const lastUpdate = await this.userStore.getActualFromDynamicData(DynamicDataTypeEnum.interestLastUpdate);
      if (
        this.rootStore &&
        suggestedRecalculationPeriod &&
        suggestedCalculationByLastXItems &&
        savedInterestItemsList &&
        (forceUpdate ||
          !lastUpdate ||
          Number(lastUpdate) + 1000 * 60 * 60 * 24 * suggestedRecalculationPeriod < Date.now())
      ) {
        await suggestedRecalculation(
          this.rootStore.recommendationFields,
          savedInterestItemsList,
          [mediaTypes.Program],
          suggestedCalculationByLastXItems,
        );
        await lastValueFrom(
          updateDynamicData({
            key: DynamicDataTypeEnum.interestLastUpdate,
            value: Date.now().toString(),
          }),
        );
      }
    }
  }

  async loadUserChannelStats() {
    if (!this.userStore?.userData?.id) {
      return;
    }
    await channelStats.loadChannelStats(this.userStore.userData.id);
    this.channelStats = channelStats;
  }

  // it is better to use exponeaStore
  async getExponeaUiData(bannerType: string, pageName: string): Promise<ExponeaUiResponse | null> {
    await this.exponeaStore.init(bannerType, pageName);

    if (this.exponeaStore.isReady) {
      return this.exponeaStore.bannerData;
    }

    return null;
  }

  setCookiePopupData() {
    this.cookiePopupDetails = {
      loadExternalCookiePopup: !!this.dms.gdpr.useExternalPopup && !!this.dms.gdpr.externalPopupScriptUrl,
      externalPopupUrl: this.dms.gdpr.externalPopupScriptUrl,
    };
  }

  async setPromoScreen() {
    // @ts-expect-error TODO Fix
    this.promoAssets = await getPromoAssets(this.dms.promoScreen.collectionId, this.dms.promoScreen.options);
    tvpilJournal.log(createAppStoreEvent('PromoScreenReady'));
  }

  clearNetworkCache() {
    clearNetworkCache();
  }

  clearApiError(id: number) {
    return this.errorStore.clearApiError(id);
  }

  clearAllApiError() {
    return this.errorStore.clearAllApiError();
  }

  reset() {
    if (this.suggestedRecalculationTimeout) {
      clearTimeout(this.suggestedRecalculationTimeout);
    }
  }

  getConnectivityConfiguration() {
    // if category tree wasn't fetched rootStore will be undefined
    const { checkInterval = DEFAULT_CONNECTIVITY_INTERVAL, recoveryDelay = DEFAULT_APPLICATION_RECOVERY_DELAY } =
      this.rootStore?.connectivity || {};

    return {
      checkInterval: checkInterval * ONE_SECOND_MILLISECONDS,
      recoveryDelay: Math.ceil(Math.random() * recoveryDelay) * ONE_SECOND_MILLISECONDS,
    };
  }
}

registerContainers();

container.register<AppStore>('appStore', { useClass: AppStore }, { lifecycle: Lifecycle.Singleton });

export const appStore = container.resolve<AppStore>('appStore');
